/**
 * @file        metrika.ts
 * @description Single goal-tracking entry point of the project. Every goal
 *              is DUAL-WRITTEN: to Yandex Metrika (reachGoal) and to our
 *              own collector POST /api/track. All NEW-2 series consumers
 *              (forms, messenger/phone clicks, tool success events,
 *              auth/channel events) call trackGoal() from here instead of
 *              `window.ym(...)` directly.
 * @runs        Browser (client component imports only — SSR-safe via window guard)
 * @universal   trackGoal — the ONE goal-tracking entry point. Call sites
 *              never touch window.ym and never call /api/track directly
 *              (series goals-tracking-order, invariant I2). Registered in
 *              knowledge/universals/tools.md.
 * @rule        GUARD ORDER MATTERS. The SSR guard returns early for BOTH
 *              branches. The `window.ym` guard skips ONLY the Metrika call:
 *              a visitor with an ad-blocker has no window.ym, and their
 *              click must still reach /api/track. That asymmetry is the
 *              whole point of the first-party log (invariant I8) — the two
 *              reports diverge by design, first-party reads HIGHER.
 * @rule        Counter ID 106847609 lives ONLY in this file. Other call sites
 *              MUST NOT hardcode the number. If counter ever changes, single
 *              edit here covers the whole codebase.
 * @rule        trackGoal MUST NEVER throw. Metrika may be missing for many
 *              legitimate reasons: ad-blocker, network failure to mc.yandex.ru,
 *              early click before init script finishes, SSR. Throwing breaks
 *              the user's actual click handler — the analytics call is
 *              best-effort, not load-bearing.
 * @rule        trackGoal MUST be synchronous and non-blocking. Click handlers
 *              that need to navigate (tel:, https://, target=_blank) cannot
 *              wait for analytics. Both the ym() call and the /api/track
 *              delivery are fire-and-forget; no await, no Promise return,
 *              no response is ever read.
 * @rule        sendBeacon FIRST, keepalive fetch only as fallback. A click
 *              on tel:/messenger links tears the page down immediately, and
 *              keepalive fetch is not guaranteed to survive that window;
 *              sendBeacon is. The interrupted request is the NORM here, not
 *              the edge case.
 * @rule        Only window.location.pathname is sent — never href, never
 *              search. Same PII rationale as the pagePath rule in LeadForm
 *              (ADR N-LEAD-PAGEPATH-1); the server drops query strings too
 *              (invariant I7), this is the first of the two locks.
 * @rule        SSR-safe — `typeof window !== "undefined"` guard required.
 *              Next.js 16 renders client components on the server during
 *              initial paint; `window` does not exist there. The pattern
 *              mirrors src/lib/leadPersistence.ts.
 * @rule        DO NOT log to console on the no-op path (window absent,
 *              ym absent, ad-blocker). Console noise on every page load
 *              for users who block analytics is bad UX. Errors during the
 *              actual ym() call are caught and silently swallowed for the
 *              same reason.
 */

// @rule Yandex Metrika counter ID for jckauto.ru. Single source of truth.
//       Hardcoding this number elsewhere is forbidden — import trackGoal
//       and pass the goal identifier instead.
const YM_COUNTER_ID = 106847609;

// Tell TypeScript that window.ym may exist as the Metrika global. The
// declaration is intentionally loose (any[] args, void return) — Metrika's
// real signature is overloaded across init/reachGoal/hit/extLink/etc and
// we only need reachGoal here. A stricter type would just rule out valid
// Metrika calls without adding safety.
declare global {
  interface Window {
    ym?: (counterId: number, action: string, ...args: unknown[]) => void;
  }
}

/**
 * Send the goal to our own collector POST /api/track. Never throws.
 *
 * @input  goalId — the same identifier passed to trackGoal
 * @output void — nothing is awaited, no response is read
 * @important Not exported on purpose: /api/track has exactly one caller
 *            (invariant I2). Delivery is best-effort — a failure here must
 *            be indistinguishable from success for the click handler.
 */
function sendFirstParty(goalId: string): void {
  try {
    const body = JSON.stringify({ goal: goalId, path: window.location.pathname });
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon("/api/track", new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {
      // Swallowed by design. Analytics is best-effort.
    });
  } catch {
    // Swallowed by design. Analytics is best-effort.
  }
}

/**
 * Fire a Yandex Metrika reachGoal event for the given goal identifier.
 *
 * The goal identifier MUST match a goal of type "JavaScript event"
 * configured in the Metrika UI for counter 106847609. If the identifier
 * does not match a configured goal, Metrika silently ignores the call —
 * no error, no warning, and the goal will simply show 0 in reports.
 * This is intentional Metrika behavior and not something we can detect
 * client-side.
 *
 * The goal is ALSO sent to our own collector POST /api/track, which counts
 * it regardless of whether the Metrika goal exists in the UI and regardless
 * of ad-blockers. Both deliveries are fire-and-forget.
 *
 * The function is best-effort: if Metrika is unavailable (SSR, ad-blocker,
 * init not yet finished, network blocked), that branch is a silent no-op —
 * the first-party branch still runs. Click handlers calling trackGoal can
 * rely on it never throwing and never blocking.
 *
 * @param goalId Stable goal identifier matching a Metrika UI goal.
 *               Examples: "lead_submit", "phone_header_desktop",
 *               "tg_floating", "tool_calculator_calculate".
 *               Must be a non-empty string of [a-z0-9_]+ per Metrika
 *               JavaScript-event identifier rules.
 *
 * @example
 *   // In a click handler:
 *   onClick={() => {
 *     trackGoal("phone_header_desktop");
 *     // … rest of handler (or just let the <a> navigate naturally)
 *   }}
 *
 * @example
 *   // After a successful form submit:
 *   const res = await fetch("/api/lead", …);
 *   if (res.ok) {
 *     trackGoal("lead_submit");
 *     trackGoal(`lead_${stableSourceLabel}`);
 *   }
 */
export function trackGoal(goalId: string): void {
  if (typeof window === "undefined") return;

  // Metrika branch — skipped alone when ym is absent (ad-blocker, init not
  // finished, network to mc.yandex.ru blocked).
  if (typeof window.ym === "function") {
    try {
      window.ym(YM_COUNTER_ID, "reachGoal", goalId);
    } catch {
      // Swallowed by design. Analytics is best-effort.
    }
  }

  // First-party branch — runs regardless of Metrika's fate.
  sendFirstParty(goalId);
}
